import config from './config';

function invokeProfileLoadedHandler(handleProfileLoaded, profile) {
    try {
        handleProfileLoaded(profile);
    } catch (error) {
        console.error('[whenProfileLoaded] callback failed', error);
    }
}

export function whenProfileLoaded(handleProfileLoaded) {
    const mountIndicator = `_sw_event_${config.profileLoadedEventName}`;
    const profileCacheKey = `_sw_profile_${config.profileLoadedEventName}`;

    if (Object.prototype.hasOwnProperty.call(window, profileCacheKey)) {
        invokeProfileLoadedHandler(handleProfileLoaded, window[profileCacheKey]);
        return;
    }

    document.addEventListener(config.profileLoadedEventName, function (e) {
        const profile = e.detail?.profile ?? e.profile;
        window[profileCacheKey] = profile;
        window[mountIndicator] = e;
        invokeProfileLoadedHandler(handleProfileLoaded, profile);
    });
}

export function whenGoogleAnalyticsHasMounted(handleGoogleAnalyticsMount) {
    const mountIndicator = `_sw_event_${config.googleAnalytics.hasMountedEventName}`;
    if (window[mountIndicator]) {
        handleGoogleAnalyticsMount();
    }

    document.addEventListener(config.googleAnalytics.hasMountedEventName, e => {
        window[mountIndicator] = e;
        handleGoogleAnalyticsMount();
    });
}

/**
 * The DOMContentLoaded event fires when the HTML document has been completely parsed, 
 * and all deferred scripts (<script defer src="…"> and <script type="module">) have 
 * downloaded and executed. It doesn't wait for other things like images, subframes, 
 * and async scripts to finish loading.
 * 
 * @param {(event: Event) => void} handleContentLoaded 
 */
export function whenDomContentLoaded(handleContentLoaded) {
    if (window["DOMContentLoaded"]) {
        handleContentLoaded(window["DOMContentLoaded"]);
    }

    document.addEventListener("DOMContentLoaded", (event) => {
        handleContentLoaded(event);
    });      
}

/**
 * The load event is fired when the whole page has loaded, including all dependent resources 
 * such as stylesheets, scripts, iframes, and images, except those that are loaded lazily. 
 * This is in contrast to DOMContentLoaded, which is fired as soon as the page DOM has been 
 * loaded, without waiting for resources to finish loading.
 * 
 * @param {Function} handlePageLoaded 
 * @returns {void}
 */
export function whenPageLoaded(handlePageLoaded) {
    if (window._sw_hasPageLoaded) {
        handlePageLoaded();
        return;
    }

    document.readyState === 'loading'
        ? document.addEventListener('DOMContentLoaded', handlePageLoaded)
        : handlePageLoaded();
    window._sw_hasPageLoaded = true;
}
